import { DownloadOutlined, EllipsisOutlined } from '@ant-design/icons';
import { Button, Dropdown, Spin, Tooltip } from 'antd';
import classNames from 'classnames';
import { useContext, useState } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { Line } from 'app/components/pages/GiftCards/details/Line/Line';
import { UserContext } from 'app/context/UserContext/UserContext';
import { useGenerateBookingReceipt } from 'app/hooks/data/adminBookingDetails/useAdminBookingReceipt';
import { useDownloadBill } from 'app/hooks/data/bills/useDownloadBill';
import { config } from 'app/redux/store';
import { type BookingDetails } from 'app/typings/adminBookings/bookings';
import { BillStatus } from 'app/typings/bills';
import { isFuture } from 'app/utils/dayjs';
import { formatPSP } from 'app/utils/psp';

import './Payment.scss';
import { CancelModal } from './_components/CancelModal';

type Props = {
  setIsVoucherModalVisible: (bool: boolean) => void;
  bookingDetails: BookingDetails;
  setRefundModalVisible: (bool: boolean) => void;
};

export const Payment = ({
  setIsVoucherModalVisible,
  bookingDetails,
  setRefundModalVisible,
}: Props) => {
  const { currencyFormatter } = useContext(CurrencyContext);

  const {
    chargedAmount,
    cardInfo,
    paymentAdvanced,
    creditAmount,
    totalAmount,
    fees,
    psp,
    status,
  } = bookingDetails.bookingSummary;

  const { voucher, bookingSummary } = bookingDetails;

  const {
    couponCode,
    couponAmount,
    refusalReason,
    billReference,
    billId,
    billStatus,
  } = paymentAdvanced;

  const canDownloadBill =
    !!billId &&
    (billStatus === BillStatus.GENERATED || billStatus === BillStatus.SENT);

  const { mutate: downloadBill, isPending: isDownloadingBill } =
    useDownloadBill();

  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);

  const { token, isAdmin } = useContext(UserContext);

  const isDownloadDisabled =
    !bookingSummary.paymentReceiptPath ||
    isFuture(new Date(bookingSummary.checkout));
  const isGenerateDisabled = isFuture(new Date(bookingSummary.checkout));

  const { mutateAsync: generateBookingReceipt } = useGenerateBookingReceipt(
    bookingSummary.code
  );

  const buttonDownloadReceipt = () => {
    const renderButton = () => (
      <Button
        className="button"
        icon={<DownloadOutlined />}
        disabled={isDownloadDisabled}
        onClick={() => {
          window.location.replace(
            isDownloadDisabled
              ? '#'
              : `${config.apiUrl}/bookings/${bookingSummary.code}/payment-receipt?token=${token}`
          );
        }}
      >
        Receipt
      </Button>
    );

    return isDownloadDisabled ? (
      <Tooltip
        placement="top"
        title={
          isGenerateDisabled
            ? 'The payment receipt will be accessible after the user has checked out.'
            : 'You have to manually generate receipt for booking before 2024'
        }
      >
        {renderButton()}
      </Tooltip>
    ) : (
      renderButton()
    );
  };

  const items = [
    {
      key: 'generate',
      label: 'Generate new receipt',
      onClick: async () => await generateBookingReceipt(),
      disabled: isGenerateDisabled,
    },
    {
      key: 'cancelBooking',
      label: 'Cancel booking',
      onClick: () => setIsCancelModalOpen(true),
    },
  ];

  const actionButtonDownloadReceipt = () => (
    <>
      {buttonDownloadReceipt()}
      <Dropdown trigger={['click']} menu={{ items }}>
        <Button className="icon-btn">
          <EllipsisOutlined className="icon" size={16} />
        </Button>
      </Dropdown>
    </>
  );

  const renderReceiptButton = () =>
    isAdmin ? actionButtonDownloadReceipt() : buttonDownloadReceipt();

  const handleDownloadBill = async () => {
    if (!canDownloadBill || isDownloadingBill) {
      return;
    }

    downloadBill(billId);
  };

  return (
    <div className="bloc">
      <div className="title-with-button2">
        <div className="label">Payment</div>
        <div className="header-buttons">
          {!voucher && (
            <Button
              className="button"
              onClick={() => setIsVoucherModalVisible(true)}
            >
              Voucher
            </Button>
          )}
          {['accepted', 'cancelled'].includes(status) && (
            <Button
              className="button"
              onClick={() => setRefundModalVisible(true)}
            >
              Refund
            </Button>
          )}
          {token && status === 'accepted' && renderReceiptButton()}
        </div>
      </div>
      <div className="generic-content">
        {psp !== undefined && (
          <Line title="Payment provider" value={formatPSP(psp)} />
        )}
        <Line
          title="Bill reference"
          value={
            <>
              {billReference && (
                <Tooltip title={canDownloadBill ? 'Download bill' : undefined}>
                  <div
                    className={classNames('bill-reference', {
                      'is-downloadable': canDownloadBill,
                      'is-downloading': isDownloadingBill,
                    })}
                    onClick={canDownloadBill ? handleDownloadBill : undefined}
                  >
                    {canDownloadBill && (
                      <>
                        <Spin spinning={isDownloadingBill} size="small" />
                        {!isDownloadingBill && <DownloadOutlined />}
                      </>
                    )}
                    <span>{billReference}</span>
                  </div>
                </Tooltip>
              )}
            </>
          }
        />
        {!!cardInfo && (
          <Line title="User card info" value={`•••• ${cardInfo.last4}`} />
        )}
        <Line
          title="Total paid"
          value={currencyFormatter(totalAmount + fees)}
        />
        <Line
          title="Credit card amount"
          value={currencyFormatter(chargedAmount)}
        />
        {couponCode !== undefined && (
          <Line title="Coupon code" value={couponCode} />
        )}
        {couponAmount !== undefined && couponAmount !== 0 && (
          <Line title="Coupon amount" value={currencyFormatter(couponAmount)} />
        )}
        {creditAmount !== 0 && (
          <Line title="Credit amount" value={currencyFormatter(creditAmount)} />
        )}
        {refusalReason !== undefined && (
          <Line title="Refusal reason" value={refusalReason} />
        )}
      </div>
      <CancelModal
        code={bookingSummary.code}
        isModalOpen={isCancelModalOpen}
        setIsModalOpen={setIsCancelModalOpen}
      />
    </div>
  );
};
